import pako from "pako";
import { handlePing, handleMarketData } from './eventHandler';
import { initializeSubscriptions } from './eventSubscriber';
import reduxStore from 'store';
import Config from 'config'

const RECONNECTION_ATTEMPTS = 15;
const RECONNECTION_DELAY = 3000;

class HTXWebSocket {
  constructor(url) {
    this.url = url;
    this.socket = null;
    this.reconnectAttempts = 0;
    this.subscriptions = new Map();
    this.messageHandlers = new Map();
    this.isConnected = false;
    this.state = {}
    this.updateState = ()=>{}
  }

  setStateAndUpdateFunction({state, setState}){
    this.state = state
    this.updateState = setState
  }

  connect() {
    try {
      this.socket = new WebSocket(this.url);
      this.setupEventHandlers();
    } catch (error) {
      console.error('WebSocket connection error:', error);
      this.handleReconnect();
    }
  }

  setupEventHandlers() {
    if (!this.socket) return;

    this.socket.onopen = () => {
      console.log('WebSocket connected');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      // Resubscribe to any existing subscriptions
      this.resubscribe();
    };

    this.socket.onmessage = (event) => {
      this.handleMessage(event);
    };

    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    this.socket.onclose = () => {
      console.log('WebSocket disconnected');
      this.isConnected = false;
      this.handleReconnect();
    };
  }

  async handleMessage(event) {
    try {
      let data;
      const raw = event.data;

      // Handle binary messages (GZIP compressed)
      if (raw instanceof Blob || raw instanceof ArrayBuffer || raw instanceof Uint8Array) {
        try {
          // Convert Blob to ArrayBuffer if needed
          const arrayBuffer = raw instanceof Blob 
            ? await raw.arrayBuffer() 
            : raw;
            
          // Convert to Uint8Array for pako
          const uint8Array = new Uint8Array(arrayBuffer);
          
          // Decompress the binary data
          const decompressed = pako.inflate(uint8Array, { to: 'string' });
          data = JSON.parse(decompressed);
        } catch (e) {
          // If decompression fails, try parsing as regular text
          console.warn('Failed to decompress binary message, trying as text:', e);
          const text = raw.toString();
          // data = JSON.parse(text);
        }
      } else {
        // Handle text messages
        const text = typeof raw === 'string' ? raw : raw.toString();
        data = JSON.parse(text);
      }

      // Handle ping messages
      if (data?.ping) {
        handlePing({ data, socket: this.socket });
        return;
      }

      if (data?.ch?.includes('market.')) {
        // Handle market data
        handleMarketData(data, this.updateState);
      } 
    } catch (error) {
      console.error('Error processing message:', error);
      // Log the raw message for debugging
      console.error('Raw message:', event.data);
    }
  }

  on(event, callback) {
    if (!this.messageHandlers.has(event)) {
      this.messageHandlers.set(event, []);
    }
    this.messageHandlers.get(event).push(callback);
  }

  off(event, callback) {
    if (!this.messageHandlers.has(event)) return;
    
    if (callback) {
      const handlers = this.messageHandlers.get(event).filter(handler => handler !== callback);
      this.messageHandlers.set(event, handlers);
    } else {
      this.messageHandlers.delete(event);
    }
  }

  send(data) {
    if (this.isConnected && this.socket && this.socket.readyState === WebSocket.OPEN) {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.socket.send(message);
      return true;
    }
    console.warn('WebSocket is not connected');
    return false;
  }

  subscribe(channel) {
    this.subscriptions.set(channel, {channel, isConnected:true});
    if (!this.isConnected) {
      return false;
    }
    
    const subscription = {
      sub: channel,
      id: `${channel}_${Date.now()}`
    };
    

    return this.send(subscription);
  }

  resubscribe() {
    if (this.subscriptions.size === 0) return;
    
    this.subscriptions.forEach(subscription => {
      this.subscribe(subscription.channel);
    });
  }

  handleReconnect() {
    if (this.reconnectAttempts >= RECONNECTION_ATTEMPTS) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${RECONNECTION_ATTEMPTS})...`);
    
    setTimeout(() => {
      this.connect();
    }, RECONNECTION_DELAY * this.reconnectAttempts);
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.isConnected = false;
    }
  }

 
}

export const htxWebSocket = new HTXWebSocket(Config.HTX_SOCKET_URL || 'wss://api-aws.huobi.pro/ws');

export const initSockets = () => {
  if(htxWebSocket.isConnected) return
  htxWebSocket.connect();
  
  initializeSubscriptions(reduxStore.store?.getState()?.crypto?.markets?.markets.map((market) => ({
    ...market,
    id: market.pairName.toLowerCase().replace("/", ""),
  })));
};

export const cleanupSockets = () => {
  if (htxWebSocket.isConnected) {
    htxWebSocket.disconnect();
  }
};
